// Bank validation regex patterns
export const IFSC_CODE_REGEX = /^[A-Z]{4}0[A-Z0-9]{6}$/;
export const BANK_ACC_NUMBER_REGEX = /^\d{9,18}$/;

// PAN card validation regex pattern
export const PAN_CARD_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;

// Validation functions
export const validateIFSCCode = (ifscCode: string): boolean => {
    return IFSC_CODE_REGEX.test(ifscCode);
};

export const validateBankAccountNumber = (accountNumber: string): boolean => {
    return BANK_ACC_NUMBER_REGEX.test(accountNumber);
};

export const validatePANCard = (panNumber: string): boolean => {
    if (!panNumber || panNumber.length === 0) {
        return false;
    }
    if (!PAN_CARD_REGEX.test(panNumber)) {
        return false;
    }
    // Check if 4th character (index 3) is "P" for Individual PAN
    if (panNumber.length >= 4 && panNumber[3] !== "P") {
        return false;
    }
    return true;
};

// Validation error messages
export const VALIDATION_MESSAGES = {
    IFSC_INVALID: "IFSC code should be in format: ABCD0123456 (4 letters, 0, 6 alphanumeric)",
    ACCOUNT_NUMBER_INVALID: "Account number should be 9-18 digits only",
    ACCOUNT_NUMBER_MISMATCH: "Account numbers do not match",
    ACCOUNT_NUMBER_MIN_LENGTH: "Account number should be at least 9 digits",
    IFSC_MIN_LENGTH: "IFSC code should be at least 4 characters",
    PAN_INVALID: "PAN should be in format: ABCDE1234F (5 letters, 4 digits, 1 letter)",
    PAN_INDIVIDUAL_ONLY: "Invalid PAN: Only Individual's Pan Card will be accepted",
    PHONE_NUMBER_REQUIRED: "Please enter a valid mobile number",
    PHONE_NUMBER_INVALID: "Please enter a valid 10-digit mobile number starting with 6-9",
    CONTACT_NAME_REQUIRED: "Please enter a valid name",
    CONTACT_NAME_INVALID: "Name should contain only letters and spaces (max 50 characters)",
    CITY_REQUIRED: "Please select a city",
} as const;

// Masking utilities for sensitive data
export const maskPAN = (panNumber: string | undefined | null): string => {
    if (!panNumber || typeof panNumber !== "string" || panNumber.length < 10) {
        return "";
    }
    return panNumber.substring(0, 2) + "XXXXXXX" + panNumber.substring(9);
};

export const maskBankAccountNumber = (accountNumber: string | undefined | null): string => {
    if (!accountNumber || typeof accountNumber !== "string" || accountNumber.length < 4) {
        return "";
    }
    if (accountNumber.length <= 8) {
        // For short account numbers, mask all but last 2 digits
        return "X".repeat(accountNumber.length - 2) + accountNumber.substring(accountNumber.length - 2);
    }
    // For longer account numbers: first 4 + XXXX + last 4
    return accountNumber.substring(0, 4) + "XXXX" + accountNumber.substring(accountNumber.length - 4);
};

export const maskBankDetails = (details: Record<string, string>): Record<string, string> => {
    const masked = { ...details };
    if (masked.accountNumber) {
        masked.accountNumber = maskBankAccountNumber(masked.accountNumber);
    }
    if (masked.reEnterAccountNumber) {
        masked.reEnterAccountNumber = maskBankAccountNumber(masked.reEnterAccountNumber);
    }
    return masked;
};

// Currency value validation - checks if a formatted currency string is zero or empty
export const isCurrencyValueZero = (value: string | undefined | null): boolean => {
    if (!value) return true;
    // Remove currency symbols, commas, and whitespace, then parse as number
    const numericValue = parseFloat(value.replace(/[₹$,\s]/g, ""));
    return isNaN(numericValue) || numericValue === 0;
};
